import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { TEMA, useMobil } from '../theme';
import {
  ProducentDetalj, Kategori, Person, Prov, InköpKort, KandidatSteg, Omdömesval, Händelse, Kundvin,
  hämtaProducent, ändraProducent, hämtaKategorier, skapaKategori,
  hämtaInköpsanvändare, skapaKontakt, taBortKontakt, skapaHändelse, taBortHändelse, skapaInköp,
  kopplaLeverantör, kopplaLossLeverantör, slåIhop,
  bytSteg, skapaProver, ändraProv, taBortProv, sparaOmdöme, taBortOmdöme,
  AKTIVA, STEGNAMN, OMDÖMESNAMN, VILLKORSBAS, villkorText,
  datumText, valutabelopp, kr, felText, idag,
} from '../api/inkop';
import { sida, rubrik, underrubrik, kort, fält, knapp, knappSpök, länkknapp, etikett, tabell, th, td, tal, märke } from '../components/inkopStil';
import { KategoriEtikett } from './InkopProducenter';
import { InköpPanel } from './InkopDetalj';
import Langtext from '../components/Langtext';

// Producentkortet har två utseenden. En kandidat är en producent vi funderar
// på att ta in: kortet visar steget, proverna och omdömena. En producent vi
// köper av visar nyckeltal, aktuellt inköp, sortimentet och historiken.
// Spec: "Vinsales – spec för modulen Inköp", Producentkort och Kandidater.

const HÄNDELSETYP: Record<string, string> = { mejl: 'Mejl', anteckning: 'Anteckning', besok: 'Besök', prov: 'Prov', steg: 'Steg' };
const KATEGORIFÄRGER = ['#8FD1B1', '#AFC4DE', '#E6B3CF', '#E0A83E', '#D9A07A', '#B8A9E3'];
const KANDIDATSTEG: KandidatSteg[] = ['kontakt', 'prover', 'provade'];
const OMDÖMESFÄRG: Record<Omdömesval, 'grön' | 'guld' | 'röd'> = { ja: 'grön', kanske: 'guld', nej: 'röd' };

function lista(text: string): string[] {
  return text.split(',').map(s => s.trim()).filter(Boolean);
}

function reaktionText(r?: string | null): string {
  if (!r) return '–';
  const känd: Record<string, string> = { gillade: 'Gillade', gillade_inte: 'Gillade inte', neutral: 'Neutral' };
  return känd[r] || r.charAt(0).toUpperCase() + r.slice(1).replace(/_/g, ' ');
}

// En rad med steg där de avklarade är gröna och det aktuella guld.
function Stegrad({ namn, aktiv }: { namn: string[]; aktiv: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '11.5px' }}>
      {namn.map((n, i) => (
        <React.Fragment key={n}>
          {i > 0 && <span style={{ color: TEMA.inkFaint }}>→</span>}
          <span style={{
            padding: '3px 10px', borderRadius: '12px',
            border: `1px solid ${i === aktiv ? TEMA.accent : i < aktiv ? TEMA.positive : TEMA.border}`,
            color: i === aktiv ? TEMA.accent : i < aktiv ? TEMA.positive : TEMA.inkFaint,
          }}>{i < aktiv ? '✓ ' : ''}{n}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Kandidatens steg ─────────────────────────────────────────────────────────

function StegForm({ steg, onSpara, onAvbryt }: {
  steg: KandidatSteg;
  onSpara: (d: { datum: string; kommentar: string; vilande_till?: string }) => Promise<void>;
  onAvbryt: () => void;
}) {
  const [datum, setDatum] = useState(idag());
  const [kommentar, setKommentar] = useState('');
  const [vilandeTill, setVilandeTill] = useState('');
  const [sparar, setSparar] = useState(false);
  const kräverAnledning = steg === 'vilande' || steg === 'avstatt';
  const rubrikText: Record<KandidatSteg, string> = {
    kontakt: 'Tillbaka till Kontakt', prover: 'Prover på väg', provade: 'Provade',
    inkop: 'Starta inköp', vilande: 'Lägg som vilande', avstatt: 'Avstå',
  };
  const kanSpara = (!kräverAnledning || kommentar.trim()) && (steg !== 'vilande' || vilandeTill);

  async function skicka(e: React.FormEvent) {
    e.preventDefault();
    if (!kanSpara) return;
    setSparar(true);
    try {
      await onSpara({ datum, kommentar: kommentar.trim(), vilande_till: steg === 'vilande' ? vilandeTill : undefined });
    } finally {
      setSparar(false);
    }
  }

  return (
    <form onSubmit={skicka} style={{ marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${TEMA.border}` }}>
      <div style={{ color: TEMA.ink, fontSize: '14px', marginBottom: '10px' }}>{rubrikText[steg]}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: '10px', marginBottom: '10px' }}>
        <div>
          <label style={etikett} htmlFor="steg-datum">Datum</label>
          <input id="steg-datum" type="date" style={fält} value={datum} onChange={e => setDatum(e.target.value)} />
        </div>
        {steg === 'vilande' && (
          <div>
            <label style={etikett} htmlFor="steg-vilande">Hör av oss igen</label>
            <input id="steg-vilande" type="date" style={fält} value={vilandeTill} min={idag()} onChange={e => setVilandeTill(e.target.value)} />
          </div>
        )}
      </div>
      <label style={etikett} htmlFor="steg-kommentar">{kräverAnledning ? 'Anledning' : 'Kommentar (frivillig)'}</label>
      <input id="steg-kommentar" style={{ ...fält, marginBottom: '10px' }} value={kommentar}
        placeholder={steg === 'avstatt' ? 'Till exempel för dyrt, fel stil eller slut på årgången' : steg === 'vilande' ? 'Till exempel bra vin men inte nu' : ''}
        onChange={e => setKommentar(e.target.value)} />
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="submit" style={{ ...knapp, opacity: kanSpara && !sparar ? 1 : 0.5 }} disabled={!kanSpara || sparar}>Spara</button>
        <button type="button" style={knappSpök} onClick={onAvbryt}>Avbryt</button>
      </div>
    </form>
  );
}

function KandidatStegKort({ p, aktivtInköp, onBytt }: {
  p: ProducentDetalj; aktivtInköp?: InköpKort; onBytt: (inkopId?: string | null) => void;
}) {
  const [val, setVal] = useState<KandidatSteg | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const steg: KandidatSteg = p.kandidat_steg || 'kontakt';
  const beslutat = steg === 'inkop' || steg === 'vilande' || steg === 'avstatt';
  const aktiv = beslutat ? 3 : KANDIDATSTEG.indexOf(steg);
  const sista = beslutat ? STEGNAMN[steg] : 'Beslut';

  async function spara(d: { datum: string; kommentar: string; vilande_till?: string }) {
    if (!val) return;
    try {
      const res = await bytSteg(p.id, { steg: val, datum: d.datum, kommentar: d.kommentar || undefined, vilande_till: d.vilande_till });
      setVal(null);
      setFel(null);
      onBytt(val === 'inkop' ? res.data.inkop_id : null);
    } catch (err) {
      setFel(felText(err, 'Kunde inte byta steg.'));
    }
  }

  return (
    <div style={kort}>
      <Stegrad namn={[...KANDIDATSTEG.map(s => STEGNAMN[s]), sista]} aktiv={aktiv} />

      {(steg === 'vilande' || steg === 'avstatt') && (
        <p style={{ fontSize: '13px', margin: '12px 0 0', color: TEMA.ink }}>
          {steg === 'vilande' ? `Vilande, hör av oss ${datumText(p.vilande_till)}` : 'Avstått'}
          {p.steg_anledning ? <span style={{ color: TEMA.inkSoft }}> · {p.steg_anledning}</span> : null}
        </p>
      )}
      {steg === 'inkop' && (
        <p style={{ fontSize: '13px', margin: '12px 0 0' }}>
          {aktivtInköp
            ? <>Inköpet är på gång och visas nedan.</>
            : 'Inget inköp pågår just nu.'}
          {' '}Kandidaten blir producent när vinerna finns i Fortnox.
        </p>
      )}

      {!val && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '14px' }}>
          {!beslutat && <button style={knapp} onClick={() => setVal('inkop')}>Starta inköp</button>}
          {!beslutat && <button style={knappSpök} onClick={() => setVal('vilande')}>Vilande</button>}
          {!beslutat && <button style={knappSpök} onClick={() => setVal('avstatt')}>Avstå</button>}
          {(steg === 'vilande' || steg === 'avstatt') && (
            <>
              <button style={knappSpök} onClick={() => setVal('kontakt')}>Öppna igen</button>
              <button style={knappSpök} onClick={() => setVal('inkop')}>Starta inköp</button>
            </>
          )}
          {steg === 'inkop' && !aktivtInköp && <button style={knappSpök} onClick={() => setVal('avstatt')}>Avstå</button>}
          <span style={{ marginLeft: 'auto', fontSize: '12.5px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{ color: TEMA.inkFaint }}>Flytta till</span>
            {KANDIDATSTEG.filter(s => s !== steg).map(s => (
              <button key={s} style={{ ...länkknapp, fontSize: '12.5px' }} onClick={() => setVal(s)}>{STEGNAMN[s]}</button>
            ))}
          </span>
        </div>
      )}
      {val && <StegForm key={val} steg={val} onSpara={spara} onAvbryt={() => { setVal(null); setFel(null); }} />}
      {fel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '8px 0 0' }}>{fel}</p>}
    </div>
  );
}

// ── Prover och omdömen ───────────────────────────────────────────────────────

function NyaProver({ producentId, onSparat, onAvbryt }: { producentId: string; onSparat: () => void; onAvbryt: () => void }) {
  const [viner, setViner] = useState([{ vin: '', argang: '', antal: '' }]);
  const [mottagna, setMottagna] = useState(false);
  const [mottaget, setMottaget] = useState(idag());
  const [anteckning, setAnteckning] = useState('');
  const [fel, setFel] = useState<string | null>(null);

  async function spara(e: React.FormEvent) {
    e.preventDefault();
    const ifyllda = viner.filter(v => v.vin.trim());
    if (!ifyllda.length) return;
    try {
      await skapaProver(producentId, {
        viner: ifyllda.map(v => ({ vin: v.vin.trim(), argang: v.argang.trim() || undefined, antal_flaskor: v.antal ? Number(v.antal) : null })),
        mottaget: mottagna ? mottaget : null,
        anteckning: anteckning.trim() || undefined,
      });
      onSparat();
    } catch (err) {
      setFel(felText(err, 'Kunde inte spara proverna.'));
    }
  }

  const ändra = (n: number, f: 'vin' | 'argang' | 'antal', v: string) =>
    setViner(viner.map((x, i) => (i === n ? { ...x, [f]: v } : x)));

  return (
    <form onSubmit={spara} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: `1px solid ${TEMA.border}` }}>
      {viner.map((v, n) => (
        <div key={n} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 80px 70px', gap: '8px', marginBottom: '8px' }}>
          <input style={fält} placeholder="Vin" value={v.vin} aria-label="Vin" onChange={e => ändra(n, 'vin', e.target.value)} />
          <input style={fält} placeholder="Årgång" value={v.argang} aria-label="Årgång" onChange={e => ändra(n, 'argang', e.target.value)} />
          <input style={fält} placeholder="Fl" type="number" min="0" value={v.antal} aria-label="Antal flaskor" onChange={e => ändra(n, 'antal', e.target.value)} />
        </div>
      ))}
      <button type="button" style={{ ...länkknapp, marginBottom: '12px' }} onClick={() => setViner([...viner, { vin: '', argang: '', antal: '' }])}>+ Ett vin till</button>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px', fontSize: '13px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input type="checkbox" style={{ accentColor: TEMA.accent }} checked={mottagna} onChange={() => setMottagna(!mottagna)} />
          Redan mottagna
        </label>
        {mottagna && <input type="date" style={{ ...fält, width: 'auto' }} value={mottaget} aria-label="Mottagna" onChange={e => setMottaget(e.target.value)} />}
        {!mottagna && <span style={{ color: TEMA.inkFaint }}>Registreras som på väg</span>}
      </div>
      <input style={{ ...fält, marginBottom: '10px' }} placeholder="Anteckning, till exempel hur proverna skickas" value={anteckning} onChange={e => setAnteckning(e.target.value)} />
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="submit" style={knapp} disabled={!viner.some(v => v.vin.trim())}>Spara prover</button>
        <button type="button" style={knappSpök} onClick={onAvbryt}>Avbryt</button>
      </div>
      {fel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '8px 0 0' }}>{fel}</p>}
    </form>
  );
}

function MittOmdöme({ prov, jag, onSparat }: { prov: Prov; jag: Person; onSparat: () => void }) {
  const mitt = prov.omdomen.find(o => o.anvandare === jag.id);
  const [kommentar, setKommentar] = useState(mitt?.kommentar || '');
  const [skriver, setSkriver] = useState(false);
  const [fel, setFel] = useState<string | null>(null);

  async function välj(o: Omdömesval) {
    try {
      if (mitt?.omdome === o) {
        if (!window.confirm('Ta bort ditt omdöme?')) return;
        await taBortOmdöme(prov.id);
        setKommentar('');
      } else {
        await sparaOmdöme(prov.id, { omdome: o, kommentar: kommentar.trim() || undefined });
        if (!mitt) setSkriver(true);
      }
      onSparat();
    } catch (err) {
      setFel(felText(err, 'Kunde inte spara omdömet.'));
    }
  }

  async function sparaKommentar(e: React.FormEvent) {
    e.preventDefault();
    if (!mitt) return;
    try {
      await sparaOmdöme(prov.id, { omdome: mitt.omdome, kommentar: kommentar.trim() || undefined });
      setSkriver(false);
      onSparat();
    } catch (err) {
      setFel(felText(err, 'Kunde inte spara kommentaren.'));
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '4px' }}>
        {(Object.keys(OMDÖMESNAMN) as Omdömesval[]).map(o => {
          const vald = mitt?.omdome === o;
          const färg = { grön: TEMA.positive, guld: TEMA.accent, röd: '#D9695F' }[OMDÖMESFÄRG[o]];
          return (
            <button key={o} type="button" onClick={() => välj(o)} title={vald ? 'Klicka igen för att ta bort' : undefined}
              style={{
                padding: '3px 9px', borderRadius: '10px', cursor: 'pointer', fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px',
                border: `1px solid ${vald ? färg : TEMA.border}`, background: 'transparent', color: vald ? färg : TEMA.inkFaint,
              }}>{OMDÖMESNAMN[o]}</button>
          );
        })}
      </div>
      {mitt && !skriver && (
        <div style={{ fontSize: '12.5px', marginTop: '4px' }}>
          {mitt.kommentar ? <span style={{ color: TEMA.ink }}>{mitt.kommentar} </span> : null}
          <button type="button" style={{ ...länkknapp, fontSize: '12px' }} onClick={() => setSkriver(true)}>{mitt.kommentar ? 'Ändra' : '+ Kommentar'}</button>
        </div>
      )}
      {mitt && skriver && (
        <form onSubmit={sparaKommentar} style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
          <input autoFocus style={{ ...fält, padding: '5px 8px', fontSize: '13px' }} value={kommentar} placeholder="Vad tyckte du?" onChange={e => setKommentar(e.target.value)} />
          <button type="submit" style={{ ...knappSpök, padding: '5px 10px' }}>Spara</button>
        </form>
      )}
      {fel && <div style={{ color: '#D9695F', fontSize: '12px', marginTop: '4px' }}>{fel}</div>}
    </div>
  );
}

function ProverKort({ p, rubrikText, onÄndrat }: { p: ProducentDetalj; rubrikText: string; onÄndrat: () => void }) {
  const [ny, setNy] = useState(false);
  const ärMobil = useMobil();
  // Provarna står sida vid sida: den inloggade först, sedan alla som lämnat omdömen.
  const provare: Person[] = [p.jag];
  p.prover.forEach(pr => pr.omdomen.forEach(o => {
    if (!provare.some(x => x.id === o.anvandare)) provare.push({ id: o.anvandare, namn: o.namn || 'Okänd' });
  }));
  const mottagna = p.prover.filter(pr => pr.mottaget).length;

  async function markeraMottaget(pr: Prov) {
    await ändraProv(pr.id, { mottaget: idag() });
    onÄndrat();
  }

  return (
    <div style={kort}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
        <h2 style={underrubrik}>{rubrikText}</h2>
        {!ny && <button style={länkknapp} onClick={() => setNy(true)}>+ Registrera prover</button>}
      </div>
      {ny && <NyaProver producentId={p.id} onSparat={() => { setNy(false); onÄndrat(); }} onAvbryt={() => setNy(false)} />}
      {p.prover.length === 0 && !ny && (
        <p style={{ fontSize: '13px', margin: 0 }}>Inga prover registrerade än. Registrera dem när producenten skickar flaskor, så kan alla lämna sitt omdöme här.</p>
      )}
      {p.prover.length > 0 && (
        <>
          <p style={{ fontSize: '12.5px', margin: '0 0 8px', color: TEMA.inkFaint }}>
            {p.prover.length} viner · {mottagna} mottagna{mottagna < p.prover.length ? ` · ${p.prover.length - mottagna} på väg` : ''}
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={tabell}>
              {!ärMobil && (
                <thead>
                  <tr>
                    <th style={th}>Vin</th>
                    {provare.map(x => <th key={x.id} style={th}>{x.id === p.jag.id ? `${x.namn || 'Du'} (du)` : x.namn}</th>)}
                  </tr>
                </thead>
              )}
              <tbody>
                {p.prover.map(pr => (
                  <tr key={pr.id}>
                    <td style={{ ...td, minWidth: '180px' }}>
                      <div>{pr.vin}{pr.argang ? ` ${pr.argang}` : ''}</div>
                      <div style={{ fontSize: '11.5px', color: TEMA.inkFaint, marginTop: '2px' }}>
                        {pr.antal_flaskor ? `${pr.antal_flaskor} fl · ` : ''}
                        {pr.mottaget
                          ? `Mottaget ${datumText(pr.mottaget)}${pr.mottaget_av_namn ? ` av ${pr.mottaget_av_namn}` : ''}`
                          : <>På väg · <button style={{ ...länkknapp, fontSize: '11.5px' }} onClick={() => markeraMottaget(pr)}>Markera mottaget</button></>}
                        {' · '}
                        <button style={{ ...länkknapp, fontSize: '11.5px', color: TEMA.inkFaint }}
                          onClick={async () => { if (window.confirm(`Ta bort ${pr.vin}${pr.omdomen.length ? ` och ${pr.omdomen.length} omdömen` : ''}?`)) { await taBortProv(pr.id); onÄndrat(); } }}>Ta bort</button>
                      </div>
                      {pr.anteckning && <div style={{ fontSize: '12px', color: TEMA.inkSoft, marginTop: '2px' }}>{pr.anteckning}</div>}
                      {ärMobil && (
                        <div style={{ marginTop: '10px', display: 'grid', gap: '10px' }}>
                          {provare.map(x => (
                            <div key={x.id}>
                              <span style={{ ...etikett, marginBottom: '4px' }}>{x.id === p.jag.id ? 'Ditt omdöme' : x.namn}</span>
                              <Omdömescell pr={pr} x={x} p={p} onÄndrat={onÄndrat} />
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    {!ärMobil && provare.map(x => (
                      <td key={x.id} style={{ ...td, minWidth: '170px' }}><Omdömescell pr={pr} x={x} p={p} onÄndrat={onÄndrat} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Omdömescell({ pr, x, p, onÄndrat }: { pr: Prov; x: Person; p: ProducentDetalj; onÄndrat: () => void }) {
  if (x.id === p.jag.id) {
    return pr.mottaget
      ? <MittOmdöme prov={pr} jag={p.jag} onSparat={onÄndrat} />
      : <span style={{ fontSize: '12px', color: TEMA.inkFaint }}>När provet kommit</span>;
  }
  const o = pr.omdomen.find(y => y.anvandare === x.id);
  if (!o) return <span style={{ color: TEMA.inkFaint }}>–</span>;
  return (
    <div>
      <span style={märke(OMDÖMESFÄRG[o.omdome])}>{OMDÖMESNAMN[o.omdome]}</span>
      <span style={{ fontSize: '11px', color: TEMA.inkFaint, marginLeft: '6px' }}>{datumText(o.datum)}</span>
      {o.kommentar && <div style={{ fontSize: '12.5px', color: TEMA.ink, marginTop: '4px' }}>{o.kommentar}</div>}
    </div>
  );
}

// ── Inköp ────────────────────────────────────────────────────────────────────

// Tidigare inköp. Status och betalt enligt fakturorna i Fortnox syns på varje rad.
// Är ett tidigare inköp samma beställning som det pågående (t.ex. ett från
// Excel-importen och ett från läglistan) kan det slås ihop med det.
function Inköpshistorik({ inköp, pågående, onÖppna, onSlåIhop }: {
  inköp: InköpKort[]; pågående?: InköpKort; onÖppna: (id: string) => void; onSlåIhop: (fran: InköpKort) => void;
}) {
  const [alla, setAlla] = useState(false);
  const synliga = alla ? inköp : inköp.slice(0, 5);
  const gräns = new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);
  // Bara inköp från de senaste månaderna kan vara samma beställning som det pågående.
  const nyligen = new Date(Date.now() - 150 * 86400000).toISOString().slice(0, 10);
  const köpt = inköp.filter(i => i.status !== 'avbrutet' && (i.bestalld || i.skapad.slice(0, 10)) >= gräns)
    .reduce((s, i) => s + (i.belopp_kr || 0), 0);
  return (
    <div style={kort}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
        <h2 style={underrubrik}>Inköpshistorik</h2>
        {köpt > 0 && <span style={{ fontSize: '12.5px' }}>≈ {kr(köpt)} senaste 12 mån</span>}
      </div>
      {inköp.length === 0 && <p style={{ fontSize: '13px', margin: 0 }}>Inga tidigare inköp.</p>}
      {synliga.map(i => {
        const f = i.fakturor;
        return (
          <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '9px 0', borderTop: `1px solid ${TEMA.border}`, fontSize: '13px' }}>
            <button onClick={() => onÖppna(i.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: "'Inter', sans-serif", fontSize: '13px', flex: 1, minWidth: 0 }}>
              <span style={{ color: TEMA.ink }}>{datumText(i.bestalld || i.skapad)}</span>
              <span style={{ display: 'block', fontSize: '12px', color: TEMA.inkFaint, marginTop: '2px' }}>
                {[i.rubrik, `${i.flaskor.toLocaleString('sv-SE')} fl`, i.statusnamn].filter(Boolean).join(' · ')}
                {f && f.antal > 0 && (f.obetalda
                  ? <span style={{ color: '#D9695F' }}> · {kr(f.obetalt_kr)} obetalt</span>
                  : <span style={{ color: TEMA.positive }}> · betalt</span>)}
              </span>
            </button>
            <span style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <span style={{ color: TEMA.ink }}>{valutabelopp(i.belopp_valuta, i.valuta, 0)}</span>
              {i.belopp_kr != null && i.valuta !== 'SEK' && <span style={{ display: 'block', fontSize: '12px', color: TEMA.inkFaint, marginTop: '2px' }}>{kr(i.belopp_kr)}</span>}
              {pågående && i.status !== 'avbrutet' && (i.bestalld || i.skapad.slice(0, 10)) >= nyligen && (
                <button style={{ ...länkknapp, display: 'block', marginLeft: 'auto', fontSize: '12px', marginTop: '3px', color: TEMA.inkFaint }}
                  onClick={() => onSlåIhop(i)}>Samma som pågående?</button>
              )}
            </span>
          </div>
        );
      })}
      {inköp.length > 5 && (
        <button style={{ ...länkknapp, fontSize: '12.5px', marginTop: '6px' }} onClick={() => setAlla(a => !a)}>
          {alla ? 'Visa färre' : `Visa alla ${inköp.length}`}
        </button>
      )}
    </div>
  );
}

// ── Händelser, kontakter, kunder ─────────────────────────────────────────────

function HändelseKort({ p, onÄndrat }: { p: ProducentDetalj; onÄndrat: () => void }) {
  const [text, setText] = useState('');
  const [typ, setTyp] = useState('anteckning');
  const [datum, setDatum] = useState(idag());
  const [fel, setFel] = useState<string | null>(null);
  const [skriver, setSkriver] = useState(false);
  const öppet = skriver || !!text.trim();

  async function spara(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await skapaHändelse(p.id, { typ, sammanfattning: text.trim(), datum: datum !== idag() ? datum : undefined });
      setSkriver(false);
      setText('');
      setDatum(idag());
      onÄndrat();
    } catch (err) {
      setFel(felText(err, 'Kunde inte spara anteckningen.'));
    }
  }

  const vem = (h: Händelse) =>
    h.typ === 'mejl' ? (h.avsandare ? ` från ${h.avsandare}` : '')
      : h.utdrag?.automatiskt ? ` · följer proverna${h.skapad_av_namn ? ` (${h.skapad_av_namn})` : ''}`
      : h.skapad_av_namn ? ` · ${h.skapad_av_namn}` : '';

  return (
    <div style={kort}>
      <h2 style={underrubrik}>Händelser</h2>
      {/* Rutan stängs med Avbryt, Esc eller ett klick utanför när inget är skrivet. */}
      <form onSubmit={spara} style={{ marginBottom: '14px' }}
        onKeyDown={e => { if (e.key === 'Escape') { setText(''); setSkriver(false); (document.activeElement as HTMLElement | null)?.blur(); } }}
        onBlur={e => { if (!text.trim() && !e.currentTarget.contains(e.relatedTarget as Node | null)) setSkriver(false); }}>
        <textarea style={{ ...fält, minHeight: öppet ? '70px' : '38px', height: öppet ? undefined : '38px', resize: öppet ? 'vertical' : 'none', marginBottom: öppet ? '8px' : 0 }}
          value={text} placeholder="Skriv en anteckning från ett samtal, ett besök eller en provning"
          onFocus={() => setSkriver(true)} onChange={e => setText(e.target.value)} />
        {öppet && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'stretch' }}>
            <select style={{ ...fält, width: 'auto' }} value={typ} onChange={e => setTyp(e.target.value)} aria-label="Typ">
              <option value="anteckning">Anteckning</option><option value="besok">Besök</option><option value="prov">Prov</option>
            </select>
            <input type="date" style={{ ...fält, width: 'auto' }} value={datum} max={idag()} aria-label="Datum" onChange={e => setDatum(e.target.value)} />
            <button type="submit" style={{ ...knapp, opacity: text.trim() ? 1 : 0.5 }} disabled={!text.trim()}>Spara</button>
            <button type="button" style={knappSpök} onClick={() => { setText(''); setSkriver(false); }}>Avbryt</button>
          </div>
        )}
        {fel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '8px 0 0' }}>{fel}</p>}
      </form>
      {p.handelser.length === 0 && (
        <p style={{ fontSize: '12.5px', margin: 0, color: TEMA.inkFaint }}>Inga händelser än. Mejl med kopia till inkop@ hamnar här när mejlintaget är på plats.</p>
      )}
      {p.handelser.map(h => (
        <div key={h.id} style={{ padding: '10px 0', borderTop: `1px solid ${TEMA.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '12px' }}>
            <span style={{ color: TEMA.inkFaint }}>{datumText(h.datum)} · {HÄNDELSETYP[h.typ] || h.typ}{vem(h)}</span>
            {h.typ !== 'mejl' && h.typ !== 'steg' && (
              <button style={{ ...länkknapp, fontSize: '12px', color: TEMA.inkFaint }}
                onClick={async () => { if (window.confirm('Ta bort anteckningen?')) { await taBortHändelse(h.id); onÄndrat(); } }}>Ta bort</button>
            )}
          </div>
          {h.rubrik && <div style={{ color: TEMA.ink, fontSize: '13.5px', marginTop: '2px' }}>{h.rubrik}</div>}
          {h.sammanfattning && <Langtext text={h.sammanfattning} rader={3} style={{ color: h.typ === 'steg' ? TEMA.inkSoft : TEMA.ink, fontSize: '13.5px', marginTop: '2px' }} />}
        </div>
      ))}
    </div>
  );
}

// Leverantören i Fortnox kopplas direkt på producentkortet. Fakturorna från den
// är producentens betalningar på inköpen.
function FortnoxLeverantör({ p, onÄndrat }: { p: ProducentDetalj; onÄndrat: () => void }) {
  const l = p.leverantorer || { kopplade: [], forslag: [], hamtade: false };
  const [väljer, setVäljer] = useState(false);
  const [vald, setVald] = useState('');
  const [fel, setFel] = useState<string | null>(null);
  const bäst = l.forslag[0] && l.forslag[0].poang >= 0.6 ? l.forslag[0] : null;
  useEffect(() => { setVald(bäst?.nr || ''); }, [bäst?.nr]);
  const text = (x: { nr: string; namn?: string | null; antal: number; valutor: string[] }) =>
    `${x.namn || 'Namn saknas'} · nr ${x.nr}${x.antal ? ` · ${x.antal} ${x.antal === 1 ? 'faktura' : 'fakturor'}` : ''}${x.valutor.length ? ` i ${x.valutor.join(', ')}` : ''}`;
  const kör = async (f: () => Promise<unknown>) => {
    setFel(null);
    try { await f(); setVäljer(false); onÄndrat(); } catch (e) { setFel(felText(e, 'Kunde inte spara.')); }
  };
  if (!l.hamtade && !l.kopplade.length) {
    return <span style={{ color: TEMA.inkFaint }}>Inga leverantörsfakturor hämtade från Fortnox än.</span>;
  }
  const väljare = (
    <span style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
      <select style={{ ...fält, width: 'auto', maxWidth: '100%', padding: '4px 8px', fontSize: '13px' }} value={vald} onChange={e => setVald(e.target.value)} aria-label="Leverantör i Fortnox">
        <option value="">Välj leverantör…</option>
        {l.forslag.map(x => <option key={x.nr} value={x.nr}>{text(x)}</option>)}
      </select>
      <button style={{ ...knapp, padding: '4px 12px', fontSize: '12.5px' }} disabled={!vald}
        onClick={() => kör(() => kopplaLeverantör(vald, l.forslag.find(x => x.nr === vald)?.namn, p.id))}>Koppla</button>
      {l.kopplade.length > 0 && <button style={{ ...länkknapp, fontSize: '12.5px', color: TEMA.inkFaint }} onClick={() => setVäljer(false)}>Avbryt</button>}
    </span>
  );
  return (
    <>
      {l.kopplade.map(x => (
        <div key={x.nr} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '3px' }}>
          <span>{text(x)}</span>
          <button style={{ ...länkknapp, fontSize: '12px', color: TEMA.inkFaint }}
            onClick={() => { if (window.confirm(`Koppla loss ${x.namn || x.nr}? Fakturorna slutar visas på inköpen.`)) kör(() => kopplaLossLeverantör(x.nr, p.id)); }}>Koppla loss</button>
        </div>
      ))}
      {l.kopplade.length === 0 && (
        <>
          <div style={{ color: TEMA.inkFaint, fontSize: '12.5px', marginBottom: '6px' }}>
            Inte kopplad. Välj leverantören så visas fakturorna och betalningarna på inköpen.
          </div>
          {väljare}
        </>
      )}
      {l.kopplade.length > 0 && (väljer ? väljare
        : <button style={{ ...länkknapp, fontSize: '12.5px' }} onClick={() => setVäljer(true)}>+ En leverantör till</button>)}
      {fel && <div style={{ color: '#D9695F', fontSize: '12.5px', marginTop: '4px' }}>{fel}</div>}
    </>
  );
}

// Kontakterna läggs till och tas bort under Redigera.
function KontakterRedigera({ p, onÄndrat }: { p: ProducentDetalj; onÄndrat: () => void }) {
  const tom = { namn: '', roll: '', epost: '', telefon: '', sprak: '' };
  const [kontakt, setKontakt] = useState(tom);
  const [fel, setFel] = useState<string | null>(null);
  async function spara(e: React.FormEvent) {
    e.preventDefault();
    setFel(null);
    try {
      await skapaKontakt(p.id, kontakt);
      setKontakt(tom);
      onÄndrat();
    } catch (err) {
      setFel(felText(err, 'Kunde inte spara kontakten.'));
    }
  }
  return (
    <div style={{ marginBottom: '16px' }}>
      <span style={etikett}>Kontakter</span>
      {p.kontakter.map(k => (
        <div key={k.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '5px 0', borderTop: `1px solid ${TEMA.border}`, fontSize: '13px', color: TEMA.ink }}>
          <span>{[k.namn, k.roll, k.epost, k.telefon, k.sprak ? `skriv på ${k.sprak}` : null].filter(Boolean).join(' · ')}</span>
          <button type="button" style={{ ...länkknapp, fontSize: '12.5px' }}
            onClick={async () => { if (window.confirm(`Ta bort ${k.namn}?`)) { await taBortKontakt(k.id); onÄndrat(); } }}>Ta bort</button>
        </div>
      ))}
      <form onSubmit={spara} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
        <input style={fält} placeholder="Namn" value={kontakt.namn} required onChange={e => setKontakt({ ...kontakt, namn: e.target.value })} aria-label="Namn" />
        <input style={fält} placeholder="Roll, t.ex. ägare" value={kontakt.roll} onChange={e => setKontakt({ ...kontakt, roll: e.target.value })} aria-label="Roll" />
        <input style={fält} type="email" placeholder="E-post" value={kontakt.epost} onChange={e => setKontakt({ ...kontakt, epost: e.target.value })} aria-label="E-post" />
        <input style={fält} placeholder="Telefon" value={kontakt.telefon} onChange={e => setKontakt({ ...kontakt, telefon: e.target.value })} aria-label="Telefon" />
        <input style={fält} placeholder="Språk, t.ex. franska" value={kontakt.sprak} onChange={e => setKontakt({ ...kontakt, sprak: e.target.value })} aria-label="Språk" />
        <button type="submit" style={knappSpök}>+ Lägg till kontakt</button>
      </form>
      {fel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '6px 0 0' }}>{fel}</p>}
    </div>
  );
}

// Allt man behöver för att nå och handla med producenten, i en ruta.
function Kontaktinformation({ p, namnPå, onÄndrat }: { p: ProducentDetalj; namnPå: (id?: string | null) => string | undefined; onÄndrat: () => void }) {
  const ärMobil = useMobil();

  const rad = (etikettText: string, innehåll: React.ReactNode) => (
    <div style={{ display: 'grid', gridTemplateColumns: ärMobil ? '1fr' : '170px minmax(0, 1fr)', gap: ärMobil ? '2px' : '12px', padding: '7px 0', borderTop: `1px solid ${TEMA.border}`, fontSize: '13.5px' }}>
      <span style={{ color: TEMA.inkFaint }}>{etikettText}</span>
      <div style={{ color: TEMA.ink, minWidth: 0 }}>{innehåll}</div>
    </div>
  );

  return (
    <div style={kort}>
      <h2 style={underrubrik}>Kontaktinformation</h2>
      {rad('Kontakt', (
        <>
          {p.kontakter.length === 0 && <span style={{ color: TEMA.inkFaint }}>Ingen kontakt inlagd. Lägg till under Redigera.</span>}
          {p.kontakter.map(k => (
            <div key={k.id} style={{ marginBottom: '3px' }}>
              <span>
                {k.namn}{k.roll ? <span style={{ color: TEMA.inkFaint }}> · {k.roll}</span> : null}
                {k.epost && <> · <a href={`mailto:${k.epost}`} style={{ color: TEMA.inkSoft }}>{k.epost}</a></>}
                {k.telefon && <span style={{ color: TEMA.inkSoft }}> · {k.telefon}</span>}
                {k.sprak && <span style={{ color: TEMA.inkFaint }}> · skriv på {k.sprak}</span>}
              </span>
            </div>
          ))}
        </>
      ))}
      {rad('Land och valuta', <>{[p.region, p.land].filter(Boolean).join(', ') || 'Land saknas'} · {p.valuta}{p.agent_andel > 0 ? ` · agent ${Math.round(p.agent_andel * 100)} %` : ''}</>)}
      {!p.kandidat && rad('Betalningsvillkor', villkorText(p.betalningsvillkor))}
      {!p.kandidat && rad('Leverantör i Fortnox', <FortnoxLeverantör p={p} onÄndrat={onÄndrat} />)}
      {rad('Ansvariga', <>Producent: {namnPå(p.producentansvarig) || '–'}{!p.kandidat && <> · Logistik och ekonomi: {namnPå(p.logistikansvarig) || '–'}</>}</>)}
      {p.adress && rad('Hämtadress', p.adress)}
      {p.webbplats && rad('Webbplats', (
        <a href={p.webbplats.startsWith('http') ? p.webbplats : `https://${p.webbplats}`} target="_blank" rel="noreferrer" style={{ color: TEMA.inkSoft }}>
          {p.webbplats.replace(/^https?:\/\//, '')}
        </a>
      ))}
      {p.kategorier.length > 0 && rad('Kategorier', p.kategorier.map(k => <KategoriEtikett key={k.id} k={k} />))}
    </div>
  );
}

const REAKTIONSFÄRG = (r?: string | null) =>
  r === 'gillade' ? TEMA.positive : r === 'nej' || r === 'gillade_inte' ? '#D9695F' : TEMA.inkFaint;

// "Sturehof AB / Sturehof Matbaren" → "Sturehof Matbaren": restaurangen, inte bolaget.
const ställe = (kund?: string | null) => (kund ? kund.split(' / ').pop() || kund : '–');

function Fördelning({ g }: { g: Kundvin }) {
  const ordning = Object.entries(g.reaktioner).sort(([a], [b]) =>
    (a === 'gillade' ? 0 : a === 'nej' || a === 'gillade_inte' ? 2 : 1) - (b === 'gillade' ? 0 : b === 'nej' || b === 'gillade_inte' ? 2 : 1));
  return (
    <>
      <div style={{ display: 'flex', height: '4px', borderRadius: '2px', overflow: 'hidden', margin: '6px 0 4px', background: TEMA.border }}>
        {ordning.map(([r, n]) => (
          <div key={r} style={{ width: `${(n / g.antal) * 100}%`, background: REAKTIONSFÄRG(r), opacity: r === 'gillade' || r === 'nej' || r === 'gillade_inte' ? 1 : 0.45 }} />
        ))}
      </div>
      <div style={{ fontSize: '12px', color: TEMA.inkFaint }}>
        {ordning.map(([r, n], i) => (
          <span key={r}>{i > 0 && ' · '}<span style={{ color: REAKTIONSFÄRG(r) }}>{n} {reaktionText(r).toLowerCase()}</span></span>
        ))}
        {' · '}senast {datumText(g.senaste_datum, true)}
      </div>
    </>
  );
}

function KundprovningKort({ p }: { p: ProducentDetalj }) {
  const [öppna, setÖppna] = useState<Record<string, boolean>>({});
  const [alla, setAlla] = useState<Record<string, boolean>>({});
  if (!p.kundprovningar.length) return null;
  const totalt = p.kundprovningar.reduce((s, g) => s + g.antal, 0);
  return (
    <div style={kort}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
        <h2 style={underrubrik}>Provat hos kunder</h2>
        <span style={{ fontSize: '12px', color: TEMA.inkFaint }}>{totalt} provningar</span>
      </div>
      {p.kundprovningar.map(g => {
        const öppen = öppna[g.vin] ?? false;
        const visade = alla[g.vin] ? g.provningar : g.provningar.slice(0, 5);
        return (
          <div key={g.vin} style={{ padding: '10px 0', borderTop: `1px solid ${TEMA.border}` }}>
            <button onClick={() => setÖppna({ ...öppna, [g.vin]: !öppen })} aria-expanded={öppen}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '100%', textAlign: 'left', fontFamily: "'Inter', sans-serif" }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13.5px', color: TEMA.ink }}>
                <span>{g.vin || 'Okänt vin'}</span>
                <span style={{ color: TEMA.inkFaint, fontSize: '12px', whiteSpace: 'nowrap' }}>{g.antal} {öppen ? '▴' : '▾'}</span>
              </div>
              <Fördelning g={g} />
            </button>
            {öppen && (
              <div style={{ marginTop: '8px' }}>
                {visade.map(k => (
                  <div key={k.provning_id} style={{ padding: '5px 0', fontSize: '12.5px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '48px minmax(0, 1fr) auto', gap: '8px', alignItems: 'baseline' }}>
                      <span style={{ color: TEMA.inkFaint, whiteSpace: 'nowrap' }}>{datumText(k.datum, true)}</span>
                      <span style={{ color: TEMA.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k.kund || undefined}>
                        {ställe(k.kund)}{k.saljare ? <span style={{ color: TEMA.inkFaint }}> · {k.saljare}</span> : null}
                      </span>
                      <span style={{ color: REAKTIONSFÄRG(k.reaktion), fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', whiteSpace: 'nowrap' }}>{reaktionText(k.reaktion)}</span>
                    </div>
                    {k.kommentar && <div style={{ margin: '2px 0 0 56px', color: TEMA.inkSoft, fontStyle: 'italic' }}>{k.kommentar}</div>}
                  </div>
                ))}
                {g.provningar.length > 5 && (
                  <button style={{ ...länkknapp, fontSize: '12px', marginTop: '4px' }} onClick={() => setAlla({ ...alla, [g.vin]: !alla[g.vin] })}>
                    {alla[g.vin] ? 'Visa färre' : `Visa alla ${g.provningar.length}`}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function VinerKort({ p }: { p: ProducentDetalj }) {
  const [alla, setAlla] = useState(false);
  const viner = alla ? p.viner : p.viner.slice(0, 8);
  const sålt = p.viner.reduce((s, v) => s + (v.salt_12m || 0), 0);
  const lager = p.viner.reduce((s, v) => s + Math.max(v.lager || 0, 0), 0);
  return (
    <div style={kort}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
        <h2 style={underrubrik}>Viner i sortimentet</h2>
        {p.viner.length > 0 && (
          <span style={{ fontSize: '12.5px' }}>{sålt.toLocaleString('sv-SE')} fl sålda senaste 12 mån · {lager.toLocaleString('sv-SE')} fl i lager</span>
        )}
      </div>
      {p.viner.length === 0 ? (
        <p style={{ fontSize: '13px', margin: 0 }}>Inga artiklar hittades. Kontrollera att producentens namn i Fortnox (Manufacturer) står under Redigera.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={tabell}>
            <thead><tr><th style={th}>Vin</th><th style={{ ...th, ...tal }}>Sålt 12 mån</th><th style={{ ...th, ...tal }}>Lager</th><th style={{ ...th, ...tal }}>Listpris</th></tr></thead>
            <tbody>
              {viner.map(v => (
                <tr key={v.artikel_nr}>
                  <td style={td}>{v.namn}<div style={{ fontSize: '11px', color: TEMA.inkFaint }}>{v.artikel_nr}</div></td>
                  <td style={{ ...td, ...tal }}>{v.salt_12m.toLocaleString('sv-SE')}</td>
                  <td style={{ ...td, ...tal, color: (v.lager ?? 0) <= 0 ? '#D9695F' : TEMA.ink }}>{v.lager ?? '–'}</td>
                  <td style={{ ...td, ...tal }}>{kr(v.listpris)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {p.viner.length > 8 && (
            <button style={{ ...länkknapp, fontSize: '12.5px', marginTop: '8px' }} onClick={() => setAlla(a => !a)}>
              {alla ? 'Visa färre' : `Visa alla ${p.viner.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sidan ────────────────────────────────────────────────────────────────────

export default function InkopProducent() {
  const { id = '' } = useParams();
  // Inköpet som visas på kortet: ?inkop=<id>, annars det första som pågår.
  const [params, setParams] = useSearchParams();
  const ärMobil = useMobil();
  const [p, setP] = useState<ProducentDetalj | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const [personer, setPersoner] = useState<Person[]>([]);
  const [kategorier, setKategorier] = useState<Kategori[]>([]);
  const [redigerar, setRedigerar] = useState(false);
  const [utkast, setUtkast] = useState<any>(null);
  const [nyKategori, setNyKategori] = useState('');

  const ladda = useCallback(() => {
    hämtaProducent(id).then(r => setP(r.data)).catch(e => setFel(felText(e, 'Kunde inte hämta producenten.')));
  }, [id]);

  useEffect(() => {
    ladda();
    hämtaInköpsanvändare().then(r => setPersoner(r.data)).catch(() => {});
    hämtaKategorier().then(r => setKategorier(r.data)).catch(() => {});
  }, [ladda]);

  function börjaRedigera() {
    if (!p) return;
    setUtkast({
      namn: p.namn, land: p.land || '', region: p.region || '', webbplats: p.webbplats || '',
      valuta: p.valuta, agent_procent: Math.round((p.agent_andel || 0) * 1000) / 10,
      betalningsvillkor: (p.betalningsvillkor || []).map(v => ({ ...v, procent: Math.round(v.andel * 100) })),
      producentansvarig: p.producentansvarig || '', logistikansvarig: p.logistikansvarig || '',
      fortnox_namn: (p.fortnox_namn || []).join(', '), domaner: (p.domaner || []).join(', '),
      anteckning: p.anteckning || '', aktiv: p.aktiv, kategorier: p.kategorier.map(k => k.id),
      adress: p.adress || '', max_flaskor_per_pall: p.max_flaskor_per_pall ?? '',
    });
    setRedigerar(true);
  }

  async function spara() {
    if (!utkast) return;
    try {
      await ändraProducent(id, {
        namn: utkast.namn, land: utkast.land, region: utkast.region, webbplats: utkast.webbplats,
        valuta: utkast.valuta, agent_andel: (Number(utkast.agent_procent) || 0) / 100,
        betalningsvillkor: utkast.betalningsvillkor
          .filter((v: any) => Number(v.procent) > 0)
          .map((v: any) => ({ andel: Number(v.procent) / 100, dagar: Number(v.dagar) || 0, fran: v.fran })),
        producentansvarig: utkast.producentansvarig || null, logistikansvarig: utkast.logistikansvarig || null,
        fortnox_namn: lista(utkast.fortnox_namn), domaner: lista(utkast.domaner).map(d => d.toLowerCase().replace(/^.*@/, '')),
        anteckning: utkast.anteckning, aktiv: utkast.aktiv, kategorier: utkast.kategorier,
        adress: utkast.adress || null,
        max_flaskor_per_pall: utkast.max_flaskor_per_pall === '' ? null : Math.round(Number(utkast.max_flaskor_per_pall)) || null,
      });
      setRedigerar(false);
      ladda();
    } catch (err) {
      setFel(felText(err, 'Kunde inte spara.'));
    }
  }

  async function läggTillKategori() {
    if (!nyKategori.trim()) return;
    try {
      const res = await skapaKategori({ namn: nyKategori.trim(), farg: KATEGORIFÄRGER[kategorier.length % KATEGORIFÄRGER.length] });
      setKategorier([...kategorier, res.data]);
      setUtkast({ ...utkast, kategorier: [...utkast.kategorier, res.data.id] });
      setNyKategori('');
    } catch (err) {
      setFel(felText(err, 'Kunde inte skapa kategorin.'));
    }
  }

  async function startaInköp() {
    try {
      const res = await skapaInköp({ producent_id: id });
      setParams({ inkop: res.data.id });
      ladda();
    } catch (err) {
      setFel(felText(err, 'Kunde inte skapa inköpet.'));
    }
  }

  if (fel && !p) return <p style={{ color: '#D9695F' }}>{fel}</p>;
  if (!p) return <p style={sida}>Laddar...</p>;
  const namnPå = (pid?: string | null) => personer.find(x => x.id === pid)?.namn;
  const aktiva = p.inkop.filter(i => AKTIVA.includes(i.status));
  const tidigare = p.inkop.filter(i => !AKTIVA.includes(i.status));
  const kolumner = ärMobil ? '1fr' : 'minmax(0, 1.4fr) minmax(0, 1fr)';

  const valtId = params.get('inkop');
  const valt = p.inkop.find(i => i.id === valtId) || aktiva[0];
  const visaInköp = (inkopId: string) => { setParams({ inkop: inkopId }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const slåIhopMedPågående = async (fran: InköpKort) => {
    const mål = aktiva[0];
    if (!mål) return;
    const text = `Slå ihop inköpet ${datumText(fran.bestalld || fran.skapad)} (${fran.flaskor.toLocaleString('sv-SE')} fl) med det pågående?\n\n`
      + 'Viner, leveranser och bockar flyttas till det pågående inköpet, och det tidigare tas bort. Allt sparas i händelseloggen.';
    if (!window.confirm(text)) return;
    try {
      await slåIhop(mål.id, fran.id);
      setParams({ inkop: mål.id });
      ladda();
    } catch (e) {
      setFel(felText(e, 'Kunde inte slå ihop inköpen.'));
    }
  };
  const tidigareValt = valt && !AKTIVA.includes(valt.status);

  // Inköpet ligger på kortet, över hela bredden. Pågår inget visas bara en rad.
  const inköpsdel = (
    <div style={{ marginBottom: '16px' }}>
      {(aktiva.length > 1 || tidigareValt) && (
        <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', borderBottom: `1px solid ${TEMA.border}`, marginBottom: '12px' }}>
          {aktiva.map(i => (
            <button key={i.id} onClick={() => setParams({ inkop: i.id })} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0', fontFamily: "'Inter', sans-serif", fontSize: '13.5px',
              color: valt?.id === i.id ? TEMA.ink : TEMA.inkFaint, borderBottom: `2px solid ${valt?.id === i.id ? TEMA.accent : 'transparent'}`,
            }}>{i.rubrik || `${i.statusnamn} · ${valutabelopp(i.belopp_valuta, i.valuta, 0)}`}</button>
          ))}
          {tidigareValt && valt && (
            <span style={{ padding: '6px 0', fontSize: '13.5px', color: TEMA.ink, borderBottom: `2px solid ${TEMA.accent}` }}>
              Tidigare inköp, {datumText(valt.bestalld || valt.skapad)} · {valt.statusnamn}
              <button style={{ ...länkknapp, fontSize: '12.5px', marginLeft: '10px' }} onClick={() => setParams({})}>Stäng</button>
            </span>
          )}
        </div>
      )}
      {valt ? (
        <InköpPanel key={valt.id} id={valt.id} onÄndrat={ladda} onBorttaget={() => { setParams({}); ladda(); }} />
      ) : (
        <div style={{ ...kort, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13.5px' }}>Inget inköp pågår.</span>
          {!p.kandidat && <button style={knapp} onClick={startaInköp}>Starta inköp</button>}
        </div>
      )}
    </div>
  );

  return (
    <div style={sida}>
      <Link to="/inkop/producenter" style={{ color: TEMA.inkFaint, fontSize: '13px', textDecoration: 'none' }}>← {p.kandidat ? 'Kandidater' : 'Producenter'}</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', margin: '10px 0 18px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <h1 style={rubrik}>
            {p.namn}{!p.aktiv && <span style={{ fontSize: '14px', color: TEMA.inkFaint }}> · inaktiv</span>}
          </h1>
          {/* Producent = vi köper av dem och vinerna finns i Fortnox. Kandidat = vi pratar men har inte köpt än. */}
          <span style={{ ...märke(p.kandidat ? 'grå' : 'grön'), fontSize: '12px', padding: '3px 12px' }}>{p.kandidat ? 'Kandidat' : 'Producent'}</span>
        </div>
        <button style={knappSpök} onClick={redigerar ? () => setRedigerar(false) : börjaRedigera}>{redigerar ? 'Avbryt' : 'Redigera'}</button>
      </div>

      {fel && <p style={{ color: '#D9695F', fontSize: '13px' }}>{fel}</p>}

      {redigerar && utkast && (
        <div style={kort}>
          <KontakterRedigera p={p} onÄndrat={ladda} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '14px', marginBottom: '14px' }}>
            {([['namn', 'Namn'], ['land', 'Land'], ['region', 'Region'], ['webbplats', 'Webbplats']] as const).map(([f, t]) => (
              <div key={f}>
                <label style={etikett} htmlFor={`u-${f}`}>{t}</label>
                <input id={`u-${f}`} style={fält} value={utkast[f]} onChange={e => setUtkast({ ...utkast, [f]: e.target.value })} />
              </div>
            ))}
            <div>
              <label style={etikett} htmlFor="u-valuta">Valuta</label>
              <select id="u-valuta" style={fält} value={utkast.valuta} onChange={e => setUtkast({ ...utkast, valuta: e.target.value })}>
                <option>EUR</option><option>USD</option><option>SEK</option><option>GBP</option>
              </select>
            </div>
            <div>
              <label style={etikett} htmlFor="u-agent">Agent, % av inköpspriset</label>
              <input id="u-agent" type="number" step="0.5" min="0" style={fält} value={utkast.agent_procent} onChange={e => setUtkast({ ...utkast, agent_procent: e.target.value })} />
            </div>
            {([['producentansvarig', 'Producentansvarig'], ['logistikansvarig', 'Logistik och ekonomi']] as const).map(([f, t]) => (
              <div key={f}>
                <label style={etikett} htmlFor={`u-${f}`}>{t}</label>
                <select id={`u-${f}`} style={fält} value={utkast[f]} onChange={e => setUtkast({ ...utkast, [f]: e.target.value })}>
                  <option value="">Ingen</option>
                  {personer.map(x => <option key={x.id} value={x.id}>{x.namn}</option>)}
                </select>
              </div>
            ))}
          </div>

          <span style={etikett}>Betalningsvillkor</span>
          {utkast.betalningsvillkor.map((v: any, n: number) => (
            <div key={n} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', fontSize: '13px' }}>
              <input type="number" min="0" max="100" style={{ ...fält, width: '70px' }} value={v.procent} aria-label="Andel i procent"
                onChange={e => { const b = [...utkast.betalningsvillkor]; b[n] = { ...v, procent: e.target.value }; setUtkast({ ...utkast, betalningsvillkor: b }); }} />
              <span>% efter</span>
              <input type="number" min="0" style={{ ...fält, width: '70px' }} value={v.dagar} aria-label="Dagar"
                onChange={e => { const b = [...utkast.betalningsvillkor]; b[n] = { ...v, dagar: e.target.value }; setUtkast({ ...utkast, betalningsvillkor: b }); }} />
              <span>dagar räknat från</span>
              <select style={{ ...fält, width: 'auto' }} value={v.fran} aria-label="Räknat från"
                onChange={e => { const b = [...utkast.betalningsvillkor]; b[n] = { ...v, fran: e.target.value }; setUtkast({ ...utkast, betalningsvillkor: b }); }}>
                {Object.entries(VILLKORSBAS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              </select>
              <button type="button" style={länkknapp} onClick={() => setUtkast({ ...utkast, betalningsvillkor: utkast.betalningsvillkor.filter((_: any, i: number) => i !== n) })}>Ta bort</button>
            </div>
          ))}
          <button type="button" style={{ ...länkknapp, marginBottom: '14px' }}
            onClick={() => setUtkast({ ...utkast, betalningsvillkor: [...utkast.betalningsvillkor, { procent: 50, dagar: 0, fran: 'bestallning' }] })}>
            + Lägg till delbetalning
          </button>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label style={etikett} htmlFor="u-fortnox">Namn i Fortnox (Manufacturer), kommaseparerade</label>
              <input id="u-fortnox" style={fält} value={utkast.fortnox_namn} onChange={e => setUtkast({ ...utkast, fortnox_namn: e.target.value })} />
            </div>
            <div>
              <label style={etikett} htmlFor="u-domaner">Mejldomäner, kommaseparerade</label>
              <input id="u-domaner" style={fält} value={utkast.domaner} placeholder="akiliawines.com" onChange={e => setUtkast({ ...utkast, domaner: e.target.value })} />
            </div>
            <div>
              <label style={etikett} htmlFor="u-adress">Hämtadress</label>
              <input id="u-adress" style={fält} value={utkast.adress} onChange={e => setUtkast({ ...utkast, adress: e.target.value })} />
            </div>
            <div>
              <label style={etikett} htmlFor="u-pall">Högst flaskor per pall</label>
              <input id="u-pall" style={fält} inputMode="numeric" value={utkast.max_flaskor_per_pall} placeholder="Inget tak"
                onChange={e => setUtkast({ ...utkast, max_flaskor_per_pall: e.target.value })} />
            </div>
          </div>

          <span style={etikett}>Kategorier</span>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '8px', fontSize: '13.5px', color: TEMA.ink }}>
            {kategorier.map(k => (
              <label key={k.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input type="checkbox" style={{ accentColor: TEMA.accent }} checked={utkast.kategorier.includes(k.id)}
                  onChange={() => setUtkast({ ...utkast, kategorier: utkast.kategorier.includes(k.id) ? utkast.kategorier.filter((x: string) => x !== k.id) : [...utkast.kategorier, k.id] })} />
                <KategoriEtikett k={k} />
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', maxWidth: '360px' }}>
            <input style={fält} value={nyKategori} placeholder="Ny kategori, t.ex. Enhörning" onChange={e => setNyKategori(e.target.value)} />
            <button type="button" style={knappSpök} onClick={läggTillKategori}>Lägg till</button>
          </div>

          <label style={etikett} htmlFor="u-anteckning">Anteckning</label>
          <textarea id="u-anteckning" style={{ ...fält, minHeight: '70px', marginBottom: '14px' }} value={utkast.anteckning} onChange={e => setUtkast({ ...utkast, anteckning: e.target.value })} />

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button style={knapp} onClick={spara}>Spara</button>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', marginLeft: 'auto' }}>
              <input type="checkbox" style={{ accentColor: TEMA.accent }} checked={utkast.aktiv} onChange={() => setUtkast({ ...utkast, aktiv: !utkast.aktiv })} />
              Aktiv producent
            </label>
          </div>
        </div>
      )}

      <Kontaktinformation p={p} namnPå={namnPå} onÄndrat={ladda} />

      {p.kandidat ? (
        <>
          <KandidatStegKort p={p} aktivtInköp={aktiva[0]}
            onBytt={inkopId => { if (inkopId) setParams({ inkop: inkopId }); ladda(); }} />
          {(aktiva.length > 0 || tidigareValt) && inköpsdel}
          <div style={{ display: 'grid', gridTemplateColumns: kolumner, gap: '16px' }}>
            <div>
              <ProverKort p={p} rubrikText="Prover och omdömen" onÄndrat={ladda} />
              <HändelseKort p={p} onÄndrat={ladda} />
            </div>
            <div>
              <KundprovningKort p={p} />
              {tidigare.length > 0 && <Inköpshistorik inköp={tidigare} pågående={aktiva[0]} onÖppna={visaInköp} onSlåIhop={slåIhopMedPågående} />}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Uppifrån: var vi står just nu, vad som hänt, sortimentet och historiken. Allt i full bredd. */}
          {inköpsdel}
          <HändelseKort p={p} onÄndrat={ladda} />
          <VinerKort p={p} />
          <Inköpshistorik inköp={tidigare} pågående={aktiva[0]} onÖppna={visaInköp} onSlåIhop={slåIhopMedPågående} />
          {p.prover.length > 0 && <ProverKort p={p} rubrikText="Prover" onÄndrat={ladda} />}
          <KundprovningKort p={p} />
        </>
      )}
    </div>
  );
}
